import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type {
  AdditionalPaymentInfoInput,
  CreditCardType,
  StarkwareSignatureInput,
  SupportedCurrency,
  TokenPaymentMethod,
} from '@sorare/core/src/__generated__/globalTypes';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useGetAuthorizationApprovals } from '@sorare/core/src/hooks/useGetAuthorizationApprovals';
import { useMigrateEth } from '@sorare/core/src/hooks/useMigrateEth';
import { usePlatform } from '@sorare/core/src/hooks/usePlatform';

import type {
  PrepareAcceptOfferMutation,
  PrepareAcceptOfferMutationVariables,
} from './__generated__/usePrepareAcceptOffer.graphql';

const PREPARE_ACCEPT_OFFER_MUTATION = gql`
  mutation PrepareAcceptOfferMutation($input: prepareAcceptOfferInput!) {
    prepareAcceptOffer(input: $input) {
      authorizations {
        id
        ...useGetAuthorizationApprovals_authorizationRequest
      }
      primaryOffer {
        id
      }
      errors {
        message
        code
      }
    }
  }
  ${useGetAuthorizationApprovals.fragments.authorizationRequest}
` as TypedDocumentNode<
  PrepareAcceptOfferMutation,
  PrepareAcceptOfferMutationVariables
>;

type PrepareAcceptOfferArgs = {
  offerId?: string;
  cartId?: string;
  purchasableItemId?: string;
  supportedCurrency: SupportedCurrency;
  tokenPaymentMethod: TokenPaymentMethod;
  conversionCreditIds?: string[];
  useAvailableCredits: boolean;
  savePaymentMethod?: boolean;
  walletChallengeSignature?: StarkwareSignatureInput | null;
  attemptReference: string | null;
  signedAmount?: string;
  cardType?: CreditCardType;
  returnUrl?: string;
  signAuthorizations?: boolean;
  additionalPaymentInfo?: AdditionalPaymentInfoInput;
};

export const usePrepareAcceptOffer = () => {
  const platform = usePlatform();

  const getAuthorizationApprovals = useGetAuthorizationApprovals();

  const [prepareAcceptOfferMutation, { loading }] = useMutation(
    PREPARE_ACCEPT_OFFER_MUTATION,
    { showErrorsWithSnackNotification: true }
  );

  const { exchangeRate } = useConfigContext();
  const migrateEth = useMigrateEth();

  const prepareAcceptOffer = async ({
    offerId,
    cartId,
    purchasableItemId,
    supportedCurrency,
    tokenPaymentMethod,
    conversionCreditIds,
    useAvailableCredits,
    savePaymentMethod,
    walletChallengeSignature,
    attemptReference,
    signedAmount,
    cardType,
    returnUrl,
    signAuthorizations,
    additionalPaymentInfo,
  }: PrepareAcceptOfferArgs) => {
    const settlementInfo = {
      currency: supportedCurrency,
      exchangeRateId: idFromObject(exchangeRate.id),
      paymentMethod: tokenPaymentMethod,
      ...(conversionCreditIds && { conversionCreditIds }),
      useAvailableCredits,
      platform,
      savePaymentMethod,
      walletChallengeSignature,
      creditCardType: cardType,
      ...(returnUrl && { returnUrl }),
      ...(additionalPaymentInfo && { additionalPaymentInfo }),
    };

    if (signAuthorizations) {
      await migrateEth();
    }

    const { data } = await prepareAcceptOfferMutation({
      variables: {
        input: {
          offerId,
          cartId,
          purchasableItemId,
          settlementInfo,
          attemptReference,
          signedAmount,
        },
      },
    });

    const authorizations = data?.prepareAcceptOffer?.authorizations || [];

    const signedAuthorizations =
      (signAuthorizations &&
        (await getAuthorizationApprovals(authorizations))) ||
      undefined;

    const primaryOfferId =
      (!!cartId || !!purchasableItemId) &&
      data?.prepareAcceptOffer?.primaryOffer?.id;

    return {
      settlementInfo,
      authorizations,
      approvals: signedAuthorizations?.authorizationApprovals,
      primaryOfferId,
    };
  };

  return {
    prepareAcceptOffer,
    loading,
  };
};
